/**
 * Admin Disputes Page - Enhanced dispute management with account freezing
 * Allows admins to view disputes, responses, and freeze/unfreeze accounts
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Link2,
  ArrowLeft,
  AlertTriangle,
  Shield,
  Snowflake,
  Sun,
  Eye,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  User,
  Truck,
  Store,
  MapPin,
  Loader2,
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCw
} from 'lucide-react';

const STATUS_COLORS = {
  open: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  under_review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  escalated: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const SEVERITY_COLORS = {
  low: 'bg-slate-500/20 text-slate-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  high: 'bg-orange-500/20 text-orange-400',
  critical: 'bg-red-500/20 text-red-400'
};

const AdminDisputesEnhanced = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  
  // Expanded dispute
  const [expandedDispute, setExpandedDispute] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState({});
  
  // Frozen accounts
  const [frozenAccounts, setFrozenAccounts] = useState([]);
  const [showFrozenPanel, setShowFrozenPanel] = useState(false);
  
  // Modals
  const [freezeModal, setFreezeModal] = useState(null);
  const [freezeReason, setFreezeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (statusFilter) params.append('status', statusFilter);
      if (severityFilter) params.append('severity', severityFilter);
      
      const response = await api.get(`/disputes/?${params.toString()}`);
      setDisputes(response.data.disputes || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, severityFilter]);

  const fetchFrozenAccounts = async () => {
    try {
      const response = await api.get('/dispute-responses/frozen-accounts');
      setFrozenAccounts(response.data.frozen_accounts || []);
    } catch (error) {
      console.error('Failed to fetch frozen accounts:', error);
    }
  };

  useEffect(() => {
    fetchDisputes();
    fetchFrozenAccounts();
  }, [fetchDisputes]);

  const fetchDisputeDetails = async (disputeId) => {
    if (disputeDetails[disputeId]) return;
    
    try {
      const response = await api.get(`/disputes/${disputeId}`);
      
      // Also fetch responses
      let responses = [];
      try {
        const respResponse = await api.get(`/dispute-responses/disputes/${disputeId}`);
        responses = respResponse.data.responses || [];
      } catch (e) {
        // Responses endpoint may not exist
      }
      
      setDisputeDetails(prev => ({
        ...prev,
        [disputeId]: { ...response.data, responses }
      }));
    } catch (error) {
      console.error('Failed to fetch dispute details:', error);
    }
  };

  const toggleExpand = async (disputeId) => {
    if (expandedDispute === disputeId) {
      setExpandedDispute(null);
    } else {
      setExpandedDispute(disputeId);
      await fetchDisputeDetails(disputeId);
    }
  };

  const handleFreezeAccount = async () => {
    if (!freezeModal || !freezeReason) return;
    
    setActionLoading(true);
    try {
      await api.post('/dispute-responses/freeze-account', {
        user_id: freezeModal.user_id,
        account_type: freezeModal.account_type,
        reason: freezeReason,
        dispute_id: freezeModal.dispute_id
      });
      
      toast.success(`${freezeModal.account_type} account frozen`);
      setFreezeModal(null);
      setFreezeReason('');
      fetchFrozenAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to freeze account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfreezeAccount = async (account) => {
    setActionLoading(true);
    try {
      await api.post('/dispute-responses/unfreeze-account', {
        user_id: account.user_id,
        account_type: account.account_type,
        reason: 'Dispute resolved or reviewed'
      });
      
      toast.success('Account unfrozen');
      fetchFrozenAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfreeze account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetStatus = async (disputeId, newStatus) => {
    try {
      await api.post(`/disputes/${disputeId}/resolve`, {
        status: newStatus
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchDisputes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                DISPUTE MANAGEMENT
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowFrozenPanel(!showFrozenPanel)}
              variant="outline"
              className={frozenAccounts.length > 0 ? 'border-blue-500/50 text-blue-400' : ''}
            >
              <Snowflake className="h-4 w-4 mr-2" />
              Frozen ({frozenAccounts.length})
            </Button>
            <Button
              onClick={() => { fetchDisputes(); fetchFrozenAccounts(); }}
              variant="outline"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Frozen Accounts Panel */}
        {showFrozenPanel && frozenAccounts.length > 0 && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h3 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
              <Snowflake className="h-4 w-4" />
              Frozen Accounts ({frozenAccounts.length})
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {frozenAccounts.map((account, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-card border border-border rounded-lg flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{account.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{account.account_type}</p>
                    <p className="text-xs text-muted-foreground">
                      {account.frozen_at ? new Date(account.frozen_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleUnfreezeAccount(account)}
                    variant="outline"
                    size="sm"
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    disabled={actionLoading}
                  >
                    <Sun className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="under_review">Under Review</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
          </select>
          
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Severity</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        {/* Disputes List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-20">
            <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Disputes</h3>
            <p className="text-muted-foreground">No disputes match your filters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map((dispute) => {
              const isExpanded = expandedDispute === dispute.id;
              const details = disputeDetails[dispute.id];
              
              return (
                <div
                  key={dispute.id}
                  className="bg-card border border-border rounded-lg overflow-hidden"
                  data-testid={`dispute-${dispute.id}`}
                >
                  {/* Header */}
                  <button
                    onClick={() => toggleExpand(dispute.id)}
                    className="w-full p-5 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-0.5 text-xs rounded border ${STATUS_COLORS[dispute.status]}`}>
                            {dispute.status?.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${SEVERITY_COLORS[dispute.severity]}`}>
                            {dispute.severity}
                          </span>
                          <span className="text-xs text-muted-foreground capitalize">
                            by {dispute.raised_by_role}
                          </span>
                        </div>
                        
                        <h3 className="font-medium text-foreground mb-1">
                          {dispute.reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {dispute.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(dispute.created_at).toLocaleDateString()}
                          </span>
                          <span>Raised by: {dispute.raised_by_name}</span>
                        </div>
                      </div>
                      
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {/* Expanded */}
                  {isExpanded && details && (
                    <div className="border-t border-border p-5">
                      {/* Full Info */}
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Description</h4>
                          <p className="text-foreground text-sm">{dispute.description}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Details</h4>
                          <div className="space-y-1 text-sm">
                            <p><span className="text-muted-foreground">Package:</span> {dispute.package_id}</p>
                            <p><span className="text-muted-foreground">Order:</span> {dispute.order_id || 'N/A'}</p>
                            <p><span className="text-muted-foreground">Raiser:</span> {details.raiser?.name} ({details.raiser?.email})</p>
                          </div>
                        </div>
                      </div>

                      {/* Responses */}
                      {details.responses && details.responses.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-muted-foreground mb-3">
                            Responses ({details.responses.length})
                          </h4>
                          <div className="space-y-3">
                            {details.responses.map((response, idx) => (
                              <div
                                key={idx}
                                className="p-4 bg-muted/30 rounded-lg border-l-2 border-primary"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-foreground">
                                      {response.responder_name}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary capitalize">
                                      {response.responder_role}
                                    </span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(response.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground">{response.response_text}</p>
                                
                                {response.evidence_urls && response.evidence_urls.length > 0 && (
                                  <div className="flex gap-2 mt-3">
                                    {response.evidence_urls.map((url, i) => (
                                      <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded border border-border overflow-hidden hover:border-primary"
                                      >
                                        <img src={url} alt="Evidence" className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Evidence Snapshot */}
                      {details.evidence_snapshot && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Evidence Snapshot</h4>
                          <pre className="p-3 bg-muted/50 rounded-lg text-xs overflow-auto max-h-40">
                            {JSON.stringify(details.evidence_snapshot, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Recommendation */}
                      {details.recommendation && (
                        <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                          <h4 className="text-sm font-medium text-purple-400 mb-2">AI Recommendation</h4>
                          <p className="text-sm text-foreground mb-2">{details.recommendation.reasoning}</p>
                          <div className="flex items-center gap-4 text-xs">
                            <span>Type: {details.recommendation.resolution_type}</span>
                            <span>Liability: {details.recommendation.liability}</span>
                            <span>Confidence: {Math.round((details.recommendation.confidence || 0) * 100)}%</span>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3">
                        {/* Status Actions */}
                        {dispute.status !== 'resolved' && dispute.status !== 'rejected' && (
                          <>
                            <Button
                              onClick={() => handleSetStatus(dispute.id, 'under_review')}
                              variant="outline"
                              size="sm"
                              className="border-blue-500/50 text-blue-400"
                              disabled={dispute.status === 'under_review'}
                            >
                              Mark Under Review
                            </Button>
                            <Button
                              onClick={() => handleSetStatus(dispute.id, 'resolved')}
                              variant="outline"
                              size="sm"
                              className="border-green-500/50 text-green-400"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Resolve
                            </Button>
                            <Button
                              onClick={() => handleSetStatus(dispute.id, 'rejected')}
                              variant="outline"
                              size="sm"
                              className="border-red-500/50 text-red-400"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}

                        {/* Freeze Actions - show options based on dispute context */}
                        {dispute.status !== 'resolved' && dispute.status !== 'rejected' && (
                          <div className="flex gap-2">
                            <Button
                              onClick={() => setFreezeModal({
                                user_id: details.evidence_snapshot?.package?.driver_id,
                                account_type: 'driver',
                                dispute_id: dispute.id,
                                name: 'Driver'
                              })}
                              variant="outline"
                              size="sm"
                              className="border-blue-500/50 text-blue-400"
                              disabled={!details.evidence_snapshot?.package?.driver_id}
                            >
                              <Snowflake className="h-4 w-4 mr-1" />
                              Freeze Driver
                            </Button>
                            
                            {dispute.order_id && (
                              <Button
                                onClick={() => setFreezeModal({
                                  user_id: '', // Would need merchant user_id
                                  account_type: 'merchant',
                                  dispute_id: dispute.id,
                                  name: 'Merchant'
                                })}
                                variant="outline"
                                size="sm"
                                className="border-blue-500/50 text-blue-400"
                              >
                                <Snowflake className="h-4 w-4 mr-1" />
                                Freeze Merchant
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              variant="outline"
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              variant="outline"
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </main>

      {/* Freeze Modal */}
      {freezeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Snowflake className="h-5 w-5 text-blue-400" />
              Freeze {freezeModal.account_type} Account
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will prevent the {freezeModal.account_type} from accepting new jobs or processing transactions
              until the dispute is resolved.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason *</label>
              <textarea
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                placeholder="Enter reason for freezing account..."
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setFreezeModal(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleFreezeAccount}
                disabled={!freezeReason || actionLoading}
                className="bg-blue-500 text-white hover:bg-blue-400"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Freeze Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisputesEnhanced;
